import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';

import { verificationSchema, type VerificationValues } from './schemas';

interface VerificationFormProps {
  onSubmit: (values: VerificationValues) => void;
  onResend: () => void;
  pending: boolean;
  resending: boolean;
  /** True once a fresh code has been sent, so the person is told it happened. */
  resent?: boolean;
}

export function VerificationForm({
  onSubmit,
  onResend,
  pending,
  resending,
  resent = false,
}: VerificationFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<VerificationValues>({
    resolver: zodResolver(verificationSchema),
    defaultValues: { code: '' },
  });

  return (
    <Box
      component="form"
      onSubmit={(event) => void handleSubmit(onSubmit)(event)}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      noValidate
    >
      <Field label="Verification code" error={errors.code?.message}>
        <TextField
          // inputMode brings up a number pad on a phone without rejecting a
          // pasted code the way type="number" would.
          inputMode="numeric"
          autoComplete="one-time-code"
          // Four digits is the whole code, so there is nothing to type past it.
          slotProps={{ htmlInput: { maxLength: 4 } }}
          {...register('code')}
        />
      </Field>

      <Button type="submit" loading={pending}>
        Verify
      </Button>

      <Button variant="ghost" size="sm" onClick={onResend} loading={resending}>
        Send the code again
      </Button>

      {/* A new code really was issued before, but nothing said so, so people
          pressed the button again or kept typing the code it had just retired.
          role="status" means a screen reader is told as well. */}
      {resent ? (
        <Typography variant="body2" color="text.secondary" role="status" align="center">
          A new code is on its way. The previous one no longer works.
        </Typography>
      ) : null}
    </Box>
  );
}
